export interface DiagnosticResult {
  test: string;
  status: 'pass' | 'fail' | 'warning';
  message: string;
  data?: any;
}

export class SupbaseDiagnostics {
  private results: DiagnosticResult[] = [];

  async runAllDiagnostics(): Promise<DiagnosticResult[]> {
    this.results = [];
    
    console.log('🔍 Starting comprehensive Supabase diagnostics...');
    
    // 1. Verify Supabase connection
    await this.testSupabaseConnection();
    
    // 2. Verify table existence
    await this.verifyTableExistence();
    
    // 3. Check raw data
    await this.checkRawData();
    
    // 4. Test authentication
    await this.testAuthentication();
    
    // 5. Examine RLS policies
    await this.examineRLSPolicies();
    
    // 6. Provide recommendations
    await this.generateRecommendations();
    
    return this.results;
  }

  private addResult(test: string, status: 'pass' | 'fail' | 'warning', message: string, data?: any) {
    const result = { test, status, message, data };
    this.results.push(result);
    console.log(`${status === 'pass' ? '✅' : status === 'fail' ? '❌' : '⚠️'} ${test}: ${message}`);
    if (data) console.log('   Data:', data);
  }

  // 1. Verify Supabase Connection
  private async testSupabaseConnection() {
    try {
      const { supabase } = await import('../services/supabase');
      
      // Test basic connection
      const { data, error } = await supabase.from('users').select('count').limit(1);
      
      if (error) {
        this.addResult('Supabase Connection', 'fail', `Connection failed: ${error.message}`, error);
        return;
      }
      
      this.addResult('Supabase Connection', 'pass', 'Successfully connected to Supabase', {
        supabaseUrl: import.meta.env.VITE_SUPABASE_URL,
        hasAnonKey: !!import.meta.env.VITE_SUPABASE_ANON_KEY
      });
      
    } catch (error) {
      this.addResult('Supabase Connection', 'fail', `Connection error: ${error}`, error);
    }
  }

  // 2. Verify Table Existence
  private async verifyTableExistence() {
    try {
      const { supabase } = await import('../services/supabase');
      
      const tables = ['users', 'pick_lists', 'pick_items'];
      const tableResults: any = {};
      
      for (const table of tables) {
        try {
          const { data, error } = await supabase
            .from(table)
            .select('*')
            .limit(1);
          
          if (error) {
            tableResults[table] = { exists: false, error: error.message };
          } else {
            tableResults[table] = { exists: true, hasData: data && data.length > 0 };
          }
        } catch (err) {
          tableResults[table] = { exists: false, error: String(err) };
        }
      }
      
      const allTablesExist = Object.values(tableResults).every((result: any) => result.exists);
      
      this.addResult(
        'Table Existence', 
        allTablesExist ? 'pass' : 'fail',
        allTablesExist ? 'All required tables exist' : 'Some tables are missing',
        tableResults
      );
      
    } catch (error) {
      this.addResult('Table Existence', 'fail', `Table verification failed: ${error}`);
    }
  }

  // 3. Check Raw Data
  private async checkRawData() {
    try {
      const { supabase } = await import('../services/supabase');
      const { useAuthStore } = await import('../stores/authStore');
      
      // Get current app user for context
      const appUser = useAuthStore.getState().user;
      const targetUserId = appUser?.id || '550e8400-e29b-41d4-a716-446655440000';
      
      // Check users table
      const { data: users, error: usersError } = await supabase
        .from('users')
        .select('*');
      
      // Check pick_lists table
      const { data: pickLists, error: pickListsError } = await supabase
        .from('pick_lists')
        .select('*');
      
      // Check pick_lists specifically for our user
      const { data: userPickLists, error: userPickListsError } = await supabase
        .from('pick_lists')
        .select('*')
        .eq('assigned_to', targetUserId);
      
      // Check pick_items table
      const { data: pickItems, error: pickItemsError } = await supabase
        .from('pick_items')
        .select('*');
      
      const dataResults = {
        targetUserId,
        appUser: appUser ? { id: appUser.id, email: appUser.email } : null,
        users: {
          count: users?.length || 0,
          error: usersError?.message,
          sample: users?.slice(0, 2),
          hasDemoUser: users?.some(u => u.id === targetUserId) || false
        },
        pickLists: {
          count: pickLists?.length || 0,
          error: pickListsError?.message,
          sample: pickLists?.slice(0, 2),
          assignedToUser: userPickLists?.length || 0,
          userPickListsError: userPickListsError?.message
        },
        pickItems: {
          count: pickItems?.length || 0,
          error: pickItemsError?.message,
          sample: pickItems?.slice(0, 2)
        },
        diagnosis: {
          hasUsers: (users?.length || 0) > 0,
          hasPickLists: (pickLists?.length || 0) > 0,
          hasUserPickLists: (userPickLists?.length || 0) > 0,
          hasDemoUser: users?.some(u => u.id === targetUserId) || false,
          recommendation: (users?.length || 0) === 0 ? 
            'Database is empty - run migrations to populate demo data' :
            (userPickLists?.length || 0) === 0 ?
            'User exists but has no assigned pick lists - check user ID matching' :
            'Data looks good'
        }
      };
      
      const hasData = (users?.length || 0) > 0 && (userPickLists?.length || 0) > 0;
      const hasAnyData = (users?.length || 0) > 0 || (pickLists?.length || 0) > 0;
      
      this.addResult(
        'Raw Data Check',
        hasData ? 'pass' : hasAnyData ? 'warning' : 'fail',
        hasData ? 
          `Database contains data for user (${userPickLists?.length} pick lists)` : 
          hasAnyData ? 
          `Database has data but none assigned to current user (${targetUserId})` :
          'Database appears to be empty',
        dataResults
      );
      
    } catch (error) {
      this.addResult('Raw Data Check', 'fail', `Data check failed: ${error}`);
    }
  }

  // 4. Test Authentication
  private async testAuthentication() {
    try {
      const { supabase } = await import('../services/supabase');
      const { useAuthStore } = await import('../stores/authStore');
      
      // Check current session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      // Check current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      // Also check our app's auth store
      const appUser = useAuthStore.getState().user;
      
      const authResults = {
        hasSession: !!session,
        sessionError: sessionError?.message,
        hasUser: !!user,
        userError: userError?.message,
        hasAppUser: !!appUser,
        appUserId: appUser?.id,
        appUserEmail: appUser?.email,
        userId: user?.id,
        userEmail: user?.email,
        authMismatch: !!appUser && !user,
        recommendation: !appUser ? 'Please sign in using PIN (1234) or email (picker@warehouse.com / picker123)' : 
                      !user ? 'App user exists but Supabase session missing - this is normal for demo mode' : 
                      'Authentication working correctly'
      };
      
      // For demo purposes, consider app user authentication as valid
      const isAuthenticated = !!appUser || (!!user && !!session);
      
      this.addResult(
        'Authentication Test',
        isAuthenticated ? 'pass' : 'fail',
        isAuthenticated ? 
          (appUser ? `User authenticated in app: ${appUser.email}` : 'User authenticated via Supabase') : 
          'No authenticated user found',
        authResults
      );
      
    } catch (error) {
      this.addResult('Authentication Test', 'fail', `Authentication test failed: ${error}`);
    }
  }

  // 5. Examine RLS Policies
  private async examineRLSPolicies() {
    try {
      const { supabase } = await import('../services/supabase');
      
      // Test RLS by trying to access data with and without auth context
      const demoUserId = '550e8400-e29b-41d4-a716-446655440000';
      
      // Try to get pick lists for demo user
      const { data: pickListsForUser, error: pickListsError } = await supabase
        .from('pick_lists')
        .select('*')
        .eq('assigned_to', demoUserId);
      
      // Try to get all pick lists (should be filtered by RLS)
      const { data: allPickLists, error: allPickListsError } = await supabase
        .from('pick_lists')
        .select('*');
      
      const rlsResults = {
        pickListsForUser: {
          count: pickListsForUser?.length || 0,
          error: pickListsError?.message,
          sample: pickListsForUser?.slice(0, 2)
        },
        allPickLists: {
          count: allPickLists?.length || 0,
          error: allPickListsError?.message,
          sample: allPickLists?.slice(0, 2)
        },
        rlsWorking: (pickListsForUser?.length || 0) === (allPickLists?.length || 0)
      };
      
      this.addResult(
        'RLS Policies',
        rlsResults.rlsWorking ? 'pass' : 'warning',
        rlsResults.rlsWorking ? 'RLS policies appear to be working' : 'RLS policies may need review',
        rlsResults
      );
      
    } catch (error) {
      this.addResult('RLS Policies', 'fail', `RLS policy check failed: ${error}`);
    }
  }

  // 6. Generate Recommendations
  private async generateRecommendations() {
    const { useAuthStore } = await import('../stores/authStore');
    const appUser = useAuthStore.getState().user;
    
    const recommendations: string[] = [];
    const actions: string[] = [];
    
    // Check for common issues and provide solutions
    const authResult = this.results.find(r => r.test === 'Authentication Test');
    const dataResult = this.results.find(r => r.test === 'Raw Data Check');
    
    if (!appUser) {
      recommendations.push('🔑 Sign in to access your pick lists');
      actions.push('Use PIN: 1234 or Email: picker@warehouse.com / Password: picker123');
    }
    
    if (dataResult?.status === 'fail') {
      recommendations.push('📊 Database appears to be empty');
      actions.push('Contact your administrator to run database migrations');
      actions.push('Ensure Supabase project has the correct schema and demo data');
    }
    
    if (dataResult?.status === 'warning') {
      recommendations.push('⚠️ Data exists but none assigned to your user');
      actions.push('Check that pick lists are assigned to your user ID');
      actions.push('Verify user ID matching between app and database');
    }
    
    if (authResult?.status === 'pass' && dataResult?.status === 'pass') {
      recommendations.push('✅ System appears to be working correctly');
      actions.push('You should see pick lists on the dashboard');
    }
    
    // Always provide these general tips
    actions.push('💡 Refresh the dashboard page to reload data');
    actions.push('🔄 Try signing out and signing back in');
    
    this.addResult(
      'Recommendations',
      recommendations.length > 0 ? 'pass' : 'warning',
      recommendations.join(' • ') || 'No specific recommendations',
      {
        recommendations,
        actions,
        quickFixes: {
          signIn: 'Use PIN 1234 or email picker@warehouse.com',
          refreshData: 'Go to Dashboard and click "Refresh Data"',
          runDiagnostics: 'This diagnostics page helps identify issues'
        }
      }
    );
  }
}

export const diagnostics = new SupbaseDiagnostics();